import { useQuery } from "@tanstack/react-query";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ScatterChart, Scatter, ReferenceLine, ZAxis } from "recharts";
import { get } from "@/lib/api";
import { ChartCard, ChartTooltip, GRID } from "@/lib/charts";
import { Skel } from "@/components/app/Common";

const cellColor = (v) => (v === 1 ? "#1F4D3A" : v === 0.5 ? "#8FB5A0" : "#E6EEE8");

function Heatmap({ skills, rows }) {
  return (
    <div className="overflow-x-auto" data-testid="coverage-heatmap">
      <table className="text-xs">
        <thead><tr><th />{skills.map((s) => <th key={s} className="px-1 pb-2 font-medium text-ink-secondary"><span className="block w-16 truncate" title={s}>{s}</span></th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.application_id}>
              <td className="max-w-[140px] truncate pr-3 text-ink-secondary">{r.candidate}</td>
              {r.cells.map((c) => <td key={c.skill} className="p-0.5"><span className="block h-5 w-16 rounded" style={{ background: cellColor(c.value) }} title={`${c.skill}: ${c.value === 1 ? "found" : c.value ? "partial" : "missing"}`} /></td>)}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-2 flex gap-4 text-xs text-ink-muted">{[["Found", 1], ["Partial", 0.5], ["Missing", 0]].map(([l, v]) => <span key={l} className="flex items-center gap-1"><span className="h-3 w-3 rounded" style={{ background: cellColor(v) }} />{l}</span>)}</p>
    </div>
  );
}

export function OverviewCharts({ job }) {
  const { data, isLoading } = useQuery({ queryKey: ["job-analytics", job.id], queryFn: () => get(`/jobs/${job.id}/analytics`) });
  if (isLoading) return <Skel className="h-64" />;
  if (!data.screened) return null;
  const top = data.missing_skills[0];
  return (
    <div className="grid gap-6 lg:grid-cols-3" data-testid="job-overview-charts">
      <div className="lg:col-span-2">
        <ChartCard title="Must-have coverage (top 25)" testId="chart-heatmap"
          takeaway={`${data.heatmap.filter((r) => r.cells.every((c) => c.value === 1)).length} of the top ${data.heatmap.length} candidates cover every must-have.`}>
          <Heatmap skills={data.skills} rows={data.heatmap} />
        </ChartCard>
      </div>
      <ChartCard title="Top missing skills" testId="chart-missing" takeaway={top ? `${top.skill} is missing for ${top.count} of ${data.screened} candidates.` : "No must-have is commonly missing."}>
        <ResponsiveContainer width="100%" initialDimension={{ width: 320, height: 200 }} height={240}>
          <BarChart data={data.missing_skills} layout="vertical" margin={{ left: 10 }}>
            <CartesianGrid stroke={GRID} horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#8A8D85" }} />
            <YAxis type="category" dataKey="skill" width={90} tick={{ fontSize: 11, fill: "#575B54" }} />
            <Tooltip content={<ChartTooltip />} cursor={{ fill: "#FAF8F4" }} />
            <Bar dataKey="count" name="Candidates missing it" fill="#A35D5D" radius={[0, 6, 6, 0]} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
      <div className="lg:col-span-3">
        <ChartCard title="Talent quadrant" testId="chart-quadrant" takeaway={data.scatter.length ? `${data.scatter.filter((d) => d.resume_match >= 70 && d.personality >= 70).length} of ${data.scatter.length} assessed candidates score 70+ on both Resume Match and Personality Alignment. Bubble size shows the interview score.` : "Candidates appear here once they complete the assessment."}>
          <ResponsiveContainer width="100%" initialDimension={{ width: 320, height: 200 }} height={260}>
            <ScatterChart margin={{ left: 0, bottom: 10 }}>
              <CartesianGrid stroke={GRID} />
              <XAxis type="number" dataKey="resume_match" name="Resume Match" domain={[0, 100]} tick={{ fontSize: 11, fill: "#8A8D85" }} label={{ value: "Resume Match", position: "insideBottom", offset: -5, fontSize: 11, fill: "#8A8D85" }} />
              <YAxis type="number" dataKey="personality" name="Personality Alignment" domain={[0, 100]} tick={{ fontSize: 11, fill: "#8A8D85" }} width={30} />
              <ZAxis type="number" dataKey="interview" name="Interview score" range={[40, 420]} />
              <ReferenceLine x={70} stroke="#E3DED3" /><ReferenceLine y={70} stroke="#E3DED3" />
              <Tooltip content={<ChartTooltip />} />
              <Scatter data={data.scatter} name="Candidates" fill="#1F4D3A" isAnimationActive={false} />
            </ScatterChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}
